import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    VehicleType,
    WaypointContract,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    RoutingHelpersService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { Waypoint } from '@polaris-customerweb/sharedcomponents.user-content';
import { isUndefined } from 'lodash-es';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class WaypointResourceService {
    private baseUrl: string;

    constructor(
        private configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'v2/waypoints'
        );
    }

    getWaypointsForUser(vehicleType?: VehicleType): Observable<Waypoint[]> {
        let params = new HttpParams().set('includeDeletes', 'false');

        if (!isUndefined(vehicleType)) {
            params = params.set('apptype', vehicleType);
        }

        return this.http.get(this.baseUrl, { params }).pipe(
            map((contracts: WaypointContract[]) => {
                if (!contracts) {
                    return [];
                }

                return contracts?.map(
                    (contract) =>
                        new Waypoint(contract, this.configService.appSku)
                );
            })
        );
    }

    getPublicWaypoints(
        boundingBox: GeoBounds,
        rideType?: VehicleType
    ): Observable<Waypoint[]> {
        const body: {
            bounds: [
                { bottom: number; left: number; right: number; top: number },
            ];
            rideTypes?: VehicleType[];
        } = {
            bounds: [
                {
                    bottom: boundingBox.south,
                    left: boundingBox.west,
                    right: boundingBox.east,
                    top: boundingBox.north,
                },
            ],
        };

        if (rideType) {
            body.rideTypes = [rideType];
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'public'
        );
        let params = new HttpParams().set('skipAuthorization', 'true');
        if (!isUndefined(rideType)) {
            params = params.set('apptype', rideType);
        }

        return this.http.post(url, body, { params }).pipe(
            map((contracts: WaypointContract[]) => {
                return contracts.map(
                    (contract: WaypointContract) =>
                        new Waypoint(contract, this.configService.appSku)
                );
            })
        );
    }

    getWaypointById(waypointId: string): Observable<Waypoint> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            waypointId
        );
        return this.http
            .get<WaypointContract>(url)
            .pipe(
                map(
                    (contract: WaypointContract) =>
                        new Waypoint(contract, this.configService.appSku)
                )
            );
    }

    getPublicWaypointById(waypointId: string): Observable<Waypoint> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            waypointId
        );
        const params = new HttpParams().set('skipAuthorization', 'true');
        return this.http.get<WaypointContract>(url, { params }).pipe(
            catchError(() =>
                throwError(() => new Error('error retrieving public waypoint'))
            ),
            map(
                (contract: WaypointContract) =>
                    new Waypoint(contract, this.configService.appSku)
            )
        );
    }

    updateWaypoint(waypoint: Waypoint): Observable<Waypoint> {
        return this.http
            .put(this.baseUrl, waypoint.toContract())
            .pipe(map(() => waypoint));
    }

    createWaypoint(waypoint: Waypoint): Observable<Waypoint> {
        return this.http
            .post<WaypointContract>(this.baseUrl, waypoint.toContract())
            .pipe(
                map(
                    (contract: WaypointContract) =>
                        new Waypoint(contract, this.configService.appSku)
                )
            );
    }

    deleteWaypoint(waypointId: string): Observable<void> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            waypointId
        );
        return this.http.delete<undefined>(url);
    }
}
